/** Training library (INT-LIB-01…06): response shapes and small helpers shared by the library screens. */
import { api } from './api';
import type { LibraryRecording } from './streaming';

export type ItemType = 'VIDEO_LINK' | 'DOCUMENT' | 'STRATEGY' | 'RECORDING';
export type Level = 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
export type Status = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export interface FileInfo {
  id: string;
  filename: string;
  sizeBytes: number;
  mimeType: string;
  kind: 'pdf' | 'image' | 'word' | 'excel' | 'powerpoint' | 'text' | null;
  inline: boolean;
}

export interface Audience {
  floor: boolean;
  marketIds: string[];
  userIds: string[];
}

export interface LibraryItem {
  id: string;
  type: ItemType;
  title: string;
  description: string | null;
  url: string | null;
  video: { provider: 'youtube' | 'vimeo' | null; embedUrl: string | null } | null;
  file: FileInfo | null;
  strategy: { id: string; code: string; name: string; currentVersion: number; changedBy: { id: string; name: string } | null; changedAt: string | null } | null;
  link: string;
  collection: { id: string; title: string } | null;
  sortOrder: number;
  tags: string[];
  onboarding: boolean;
  level: Level | null;
  allowDownload: boolean;
  watermark: boolean;
  status: Status;
  publishedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  audience: { floor: boolean; markets: { id: string; code: string }[]; users: { id: string; name: string }[] } | null;
  me: { viewedAt: string | null; completedAt: string | null; required: { dueAt: string; overdue: boolean } | null };
  /** RECORDING items (INT-WEB-05, INT-SCR-04) */
  recording: LibraryRecording | null;
}

export interface Collection {
  id: string;
  title: string;
  description: string | null;
  level: Level | null;
  sortOrder: number;
  itemCount: number;
}

export interface ChecklistEntry {
  id: string;
  text: string;
  required: boolean;
}

export interface StrategyVersion {
  id: string;
  version: number;
  markets: string[];
  timeframes: string[];
  concept: string;
  setupConditions: string;
  entryTrigger: string;
  stopLossRule: string;
  takeProfitRule: string;
  riskRule: string;
  noTradeFilters: string | null;
  checklist: ChecklistEntry[];
  examples: { caption: string; url: string | null; file: FileInfo | null }[];
  relatedItemIds: string[];
  changelog: string | null;
  publishedAt: string;
  publishedBy: { id: string; name: string };
}

export interface StrategyDetail {
  item: LibraryItem;
  strategy: { id: string; code: string; name: string; currentVersion: number; following: boolean };
  version: StrategyVersion;
  versions: { id: string; version: number; publishedAt: string; publishedBy: { name: string }; changelog: string | null }[];
  related: { id: string; type: ItemType; title: string; link: string }[];
}

export interface MyLearning {
  assignmentId: string;
  dueAt: string;
  assignedBy: string;
  item: { id: string; title: string; type: ItemType; link: string };
  viewedAt: string | null;
  completedAt: string | null;
  overdue: boolean;
}

export const TYPE_LABEL: Record<ItemType, string> = { VIDEO_LINK: 'Video', DOCUMENT: 'Document', STRATEGY: 'Strategy', RECORDING: 'Recording' };
export const LEVEL_LABEL: Record<Level, string> = { BEGINNER: 'Beginner', INTERMEDIATE: 'Intermediate', ADVANCED: 'Advanced' };
export const LEVELS: Level[] = ['BEGINNER', 'INTERMEDIATE', 'ADVANCED'];

export function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export function audienceText(a: LibraryItem['audience']) {
  if (!a) return '';
  if (a.floor) return 'Whole floor';
  return [...a.markets.map((m) => `${m.code} traders`), ...a.users.map((u) => u.name)].join(', ') || 'Nobody yet';
}

/** Offset of `tz` from UTC at instant `d`, in ms (Intl only — no timezone library in the web bundle). */
function tzOffsetMs(d: Date, tz: string) {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      .formatToParts(d)
      .map((x) => [x.type, x.value]),
  );
  return Date.UTC(+p.year!, +p.month! - 1, +p.day!, +p.hour!, +p.minute!, +p.second!) - Math.floor(d.getTime() / 1000) * 1000;
}

/** A datetime-local value ("YYYY-MM-DDTHH:mm") read as wall-clock time in the user's timezone → UTC instant. */
export function zonedInputToDate(value: string, tz: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!m) return null;
  const guess = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!);
  const first = guess - tzOffsetMs(new Date(guess), tz);
  return new Date(guess - tzOffsetMs(new Date(first), tz)); // second pass settles daylight-saving edges
}

/** An instant shown as a datetime-local value in the user's timezone. */
export const dateToZonedInput = (d: Date, tz: string) => new Date(d.getTime() + tzOffsetMs(d, tz)).toISOString().slice(0, 16);

export const toAudience =(a: LibraryItem['audience']): Audience => (a ? { floor: a.floor, marketIds: a.markets.map((m) => m.id), userIds: a.users.map((u) => u.id) } : { floor: true, marketIds: [], userIds: [] });

/**
 * Opens one of an item's files through a short-lived signed link (the API checks the audience). A blank tab is
 * opened during the click so pop-up blockers allow it, then pointed at the file; downloads stay on this page.
 */
export async function openFile(itemId: string, fileId: string, download = false) {
  const tab = download ? null : window.open('', '_blank');
  if (tab) tab.opener = null;
  try {
    const link = await api<{ url: string }>(`/library/items/${itemId}/files/${fileId}/link`, { body: { download } });
    if (tab) tab.location.href = link.url;
    else window.location.assign(link.url);
  } catch (e) {
    tab?.close();
    throw e;
  }
}
